/**
 * Hooks para a seção "Custo por conversa" da Tela Qualidade IA (Gestor).
 *
 * Gate crítico: `enabled` gateado na capability `ai-usage-read` — o fetch
 * NÃO dispara sem a cap (gatear o fetch, não só o render — lição operacional).
 */
import { useQuery } from '@tanstack/react-query';

import { useCapability } from '../../hooks/use-capability';
import { api } from '../../lib/api-client';

import type {
  ConversationCostByAgentResponse,
  TopConversationsCostResponse,
} from '../supervisor/types';

export const CUSTO_CONV_KEY = ['gestor', 'qualidade-ia', 'custo-por-conversa'] as const;

export interface CustoWindow {
  fromDate: string;
  toDate: string;
}

const TOP_CONV_LIMIT = 10;

/**
 * Top conversas por custo no período.
 * `enabled` é `false` sem a cap `ai-usage-read` — o GET nunca dispara.
 * (B-1, 17/Set/2026: a cap era `ai-cost-monitoring`.)
 */
export function useTopConversationsByCost(w: CustoWindow): {
  data: TopConversationsCostResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  enabled: boolean;
} {
  const canCost = useCapability('ai-usage-read');
  const q = useQuery({
    queryKey: [...CUSTO_CONV_KEY, 'top', w.fromDate, w.toDate],
    queryFn: () =>
      api.getTopConversationsByCost({
        fromDate: w.fromDate,
        toDate: w.toDate,
        limit: TOP_CONV_LIMIT,
      }),
    enabled: canCost,
    staleTime: 60_000,
  });
  return { data: q.data, isLoading: q.isLoading, isError: q.isError, enabled: canCost };
}

/**
 * Custo por agente de uma conversa específica.
 * `enabled` é `false` sem a cap ou sem conversationId.
 */
export function useConversationCostByAgent(conversationId: string | null): {
  data: ConversationCostByAgentResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  enabled: boolean;
} {
  const canCost = useCapability('ai-usage-read');
  const enabled = canCost && conversationId !== null;
  const q = useQuery({
    queryKey: [...CUSTO_CONV_KEY, 'by-agent', conversationId ?? ''],
    queryFn: () => api.getConversationCostByAgent(conversationId!),
    enabled,
    staleTime: 60_000,
  });
  return { data: q.data, isLoading: q.isLoading, isError: q.isError, enabled };
}
